import proj4 from 'proj4';

/**
 * @function getUTMZone
 * @description Determines the UTM zone number for a given longitude.
 * @param {Number} lng - Longitude in degrees (-180 to 180)
 * **Called by:
 * @see {convertWGS84ToUTM}
 * @see {convertMultiplePointsToUTM}
 * @memberof module:Mepbay
 * @returns {Number} UTM zone number (1-60)
 */
export const getUTMZone = (lng) => {
  return Math.floor((lng + 180) / 6) + 1;
};

/**
 * @function getUTMProjection
 * @description Creates a proj4 projection string for a specific UTM zone.
 * @param {Number} zone - UTM zone number (1-60)
 * @param {boolean} isSouth - Whether the point is in southern hemisphere
 * **Called by:
 * @see {convertWGS84ToUTM}
 * @see {convertUTMToWGS84}
 * @see {convertMultiplePointsToUTM}
 * @see {convertMultiplePointsToWGS84}
 * @memberof module:Mepbay
 * @returns {String} proj4 projection definition string
 */
export const getUTMProjection = (zone, isSouth = false) => {
  const southFlag = isSouth ? '+south' : '';
  return `+proj=utm +zone=${zone} ${southFlag} +datum=WGS84 +units=m +no_defs`;
};

/**
 * @function convertWGS84ToUTM
 * @description Converts WGS84 coordinates (lat/lng) to UTM coordinates (x/y in meters). Not
 * currently called anywhere in the codebase.
 * @param {Number} lat - Latitude in degrees (-90 to 90)
 * @param {Number} lng - Longitude in degrees (-180 to 180)
 * @see {@link getUTMZone}
 * @see {@link getUTMProjection}
 * @memberof module:Mepbay
 * @returns {Object} {x: Easting in meters, y: Northing in meters, zone: UTM zone number, isSouth: hemisphere flag}
 */
export const convertWGS84ToUTM = (lat, lng) => {
  const zone = getUTMZone(lng);
  const isSouth = lat < 0;

  const wgs84 = '+proj=longlat +datum=WGS84 +no_defs';
  const utm = getUTMProjection(zone, isSouth);

  const [x, y] = proj4(wgs84, utm, [lng, lat]);

  return { x, y, zone, isSouth };
};

/**
 * @function convertUTMToWGS84
 * @description Converts UTM coordinates (x/y in meters) back to WGS84 (lat/lng).
 * @param {Number} x - Easting in meters
 * @param {Number} y - Northing in meters
 * @param {Number} zone - UTM zone number (1-60)
 * @param {boolean} isSouth - Whether the point is in southern hemisphere
 * @see {@link getUTMProjection}
 * **Called by:
 * @see {generateParabolicPoints}
 * @memberof module:Mepbay
 * @returns {Object} {lat: Latitude in degrees, lng: Longitude in degrees}
 */
export const convertUTMToWGS84 = (x, y, zone, isSouth = false) => {
  const wgs84 = '+proj=longlat +datum=WGS84 +no_defs';
  const utm = getUTMProjection(zone, isSouth);

  const [lng, lat] = proj4(utm, wgs84, [x, y]);

  return { lat, lng };
};

/**
 * @function convertMultiplePointsToUTM
 * @description Converts multiple WGS84 points to UTM using the same zone, validating that every
 * point falls within the same zone and hemisphere.
 * @param {Array<{lat: Number, lng: Number}>} points - Array of points in WGS84
 * @see {@link getUTMZone}
 * @see {@link getUTMProjection}
 * @throws {Error} If points span multiple UTM zones or hemispheres
 * **Called by:
 * @see {generateParabolicPoints}
 * @memberof module:Mepbay
 * @returns {Object} {utmPoints: Array of {x, y} coordinates in UTM, zone: UTM zone number used, isSouth: hemisphere flag}
 */
export const convertMultiplePointsToUTM = (points) => {
  if (!points || points.length === 0) {
    throw new Error('No points provided for conversion');
  }

  const firstPoint = points[0];
  const zone = getUTMZone(firstPoint.lng);
  const isSouth = firstPoint.lat < 0;

  for (const point of points) {
    const pointZone = getUTMZone(point.lng);
    const pointIsSouth = point.lat < 0;

    if (pointZone !== zone) {
      throw new Error(
        `Points span multiple UTM zones (${zone} and ${pointZone}). ` +
          'All points must be in the same UTM zone for accurate conversion.',
      );
    }

    if (pointIsSouth !== isSouth) {
      throw new Error(
        'Points span both northern and southern hemispheres. ' +
          'All points must be in the same hemisphere for accurate conversion.',
      );
    }
  }

  const wgs84 = '+proj=longlat +datum=WGS84 +no_defs';
  const utm = getUTMProjection(zone, isSouth);

  const utmPoints = points.map((point) => {
    const [x, y] = proj4(wgs84, utm, [point.lng, point.lat]);
    return { x, y };
  });

  return { utmPoints, zone, isSouth };
};

/**
 * @function convertMultiplePointsToWGS84
 * @description Converts multiple UTM points back to WGS84.
 * @param {Array<{x: Number, y: Number}>} utmPoints - Array of points in UTM
 * @param {Number} zone - UTM zone number
 * @param {boolean} isSouth - Whether points are in southern hemisphere
 * @see {@link getUTMProjection}
 * **Called by:
 * @see {generateParabolicPoints}
 * @memberof module:Mepbay
 * @returns {Array<{lat: Number, lng: Number}>} Array of points in WGS84
 */
export const convertMultiplePointsToWGS84 = (utmPoints, zone, isSouth = false) => {
  const wgs84 = '+proj=longlat +datum=WGS84 +no_defs';
  const utm = getUTMProjection(zone, isSouth);

  return utmPoints.map((point) => {
    const [lng, lat] = proj4(utm, wgs84, [point.x, point.y]);
    return { lat, lng };
  });
};
